// src/data/sisterGroups.ts
// ------------------------
// Repository for the db-205 "sister services" tables: sister_groups (a named
// organisation) and sister_group_members (which DFV Service is in which group).
// Both are STAFF-ONLY (RLS) and have NO hard delete — leaving a group is an UPDATE
// that sets deactivated. Typed via getTypedClient() against the generated schema.
// Readers return null on error / no client (logged); writers return { error }.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type SisterGroupRow = Database["public"]["Tables"]["sister_groups"]["Row"];
export type SisterMemberRow = Database["public"]["Tables"]["sister_group_members"]["Row"];

type WriteError = { message: string } | null;

// Active groups, name-ordered (the editor's "existing group" dropdown).
export async function listActiveSisterGroups(): Promise<
  Pick<SisterGroupRow, "id" | "name">[] | null
> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("sister_groups")
    .select("id, name")
    .eq("deactivated", false)
    .order("name");
  if (error) {
    logger.warn("listActiveSisterGroups failed:", error.message);
    return null;
  }
  return data ?? [];
}

// Every ACTIVE membership in one query (lists build their sister map from this —
// one extra query per load, never one per row).
export async function listActiveSisterMembers(): Promise<
  Pick<SisterMemberRow, "id" | "sister_group_id" | "shelter_id">[] | null
> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("sister_group_members")
    .select("id, sister_group_id, shelter_id")
    .eq("deactivated", false);
  if (error) {
    logger.warn("listActiveSisterMembers failed:", error.message);
    return null;
  }
  return data ?? [];
}

// Create a group. The name is trimmed; a blank name is refused before any DB call.
export async function createSisterGroup(
  name: string
): Promise<{ id: string | null; error: WriteError }> {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return { id: null, error: { message: "A group name is required." } };
  const c = getTypedClient();
  if (!c) return { id: null, error: null };
  const { data, error } = await c
    .from("sister_groups")
    .insert({ name: trimmed })
    .select("id")
    .single();
  if (error) logger.warn("createSisterGroup failed:", error.message);
  return { id: (data?.id as string | undefined) ?? null, error };
}

// Put a service into a group. The DB allows only ONE active membership per service,
// so the caller deactivates any existing one first (see saveShelterSisterGroup).
export async function addSisterMember(
  sisterGroupId: string,
  shelterId: string
): Promise<{ error: WriteError }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("sister_group_members")
    .insert({ sister_group_id: sisterGroupId, shelter_id: shelterId });
  if (error) logger.warn("addSisterMember failed:", error.message);
  return { error };
}

// Take a service out of its group: UPDATE deactivated (no-delete convention).
export async function deactivateSisterMember(memberId: string): Promise<{ error: WriteError }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("sister_group_members")
    .update({ deactivated: true, deactivated_at: new Date().toISOString() })
    .eq("id", memberId);
  if (error) logger.warn("deactivateSisterMember failed:", error.message);
  return { error };
}

// Rename a group (7 Oct 2026). Trimmed; a blank name is refused before any DB call.
// `.select("id")` so a write RLS silently filtered out (0 rows, no error) is reported.
export async function renameSisterGroup(
  groupId: string,
  name: string
): Promise<{ error: WriteError }> {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return { error: { message: "A group name is required." } };
  const c = getTypedClient();
  if (!c) return { error: null };
  const { data, error } = await c
    .from("sister_groups")
    .update({ name: trimmed })
    .eq("id", groupId)
    .select("id");
  if (error) {
    logger.warn("renameSisterGroup failed:", error.message);
    return { error };
  }
  if (!data || data.length === 0) {
    return { error: { message: "Could not rename the group (no permission, or it was retired)." } };
  }
  return { error: null };
}

// Retire a whole group for good (7 Oct 2026; "hidden for good" — David). UPDATEs only
// (no-delete convention), in this order, stopping at the first error:
//   1. switch off every ACTIVE membership of the group,
//   2. switch off the group itself (must touch 1 row, else reported),
//   3. sweep the memberships again (catches one added between 1 and 2).
// Members go FIRST: a retired group with a still-active membership would block that
// service from joining any other group (one active membership per service). Every step
// is a no-op when repeated, so retrying after a failure is safe.
export async function retireSisterGroup(groupId: string): Promise<{ error: WriteError }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const at = new Date().toISOString();
  const sweepMembers = () =>
    c
      .from("sister_group_members")
      .update({ deactivated: true, deactivated_at: at })
      .eq("sister_group_id", groupId)
      .eq("deactivated", false);

  const m1 = await sweepMembers();
  if (m1.error) {
    logger.warn("retireSisterGroup (members) failed:", m1.error.message);
    return { error: m1.error };
  }
  const g = await c
    .from("sister_groups")
    .update({ deactivated: true, deactivated_at: at })
    .eq("id", groupId)
    .select("id");
  if (g.error) {
    logger.warn("retireSisterGroup (group) failed:", g.error.message);
    return { error: g.error };
  }
  if (!g.data || g.data.length === 0) {
    return { error: { message: "Could not retire the group (no permission)." } };
  }
  const m2 = await sweepMembers();
  if (m2.error) logger.warn("retireSisterGroup (sweep) failed:", m2.error.message);
  return { error: m2.error };
}
